import type { EmployeePersona, PrototypeBootstrap } from "@peer-start/platform-contracts";
import { Card, CardHeader } from "@peer-start/ui";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import {
  careEnvironments,
  experienceDomains,
  experienceYears,
  workChallenges,
} from "../../prototype/components/onboarding/onboarding-options.js";
import { resolveOnboardingAnswers } from "../../prototype/components/onboarding/onboarding-answers.js";
import { ProfileDetailRow } from "./ProfileDetailRow.js";

function labelFor(
  options: readonly { value: string; labelKey: string }[],
  value: string,
  translate: (key: string) => string,
  fallback: string,
): string {
  const match = options.find((entry) => entry.value === value);
  return match ? translate(match.labelKey) : fallback;
}

export function ProfileOnboardingTab({
  bootstrap,
  persona,
}: {
  bootstrap: PrototypeBootstrap;
  persona: EmployeePersona;
}) {
  const { t } = useTranslation();
  const answers = resolveOnboardingAnswers({
    employeeId: persona.id,
    roleId: persona.roleId,
    workContext: persona.workContext,
    onboardingComplete: persona.onboardingComplete,
  });
  const role = bootstrap.roles.find((entry) => entry.id === answers.roleId || entry.id === persona.roleId);
  const empty = t("profile.notProvided");
  const careLabel = answers.careEnvironment
    ? labelFor(careEnvironments, answers.careEnvironment, t, empty)
    : (persona.workContext ?? empty);
  const yearsLabel = answers.years ? labelFor(experienceYears, answers.years, t, empty) : empty;
  const domainsLabel = answers.domainIds.length
    ? answers.domainIds.map((id) => labelFor(experienceDomains, id, t, id)).join(", ")
    : empty;
  const challengeLabel = answers.challenge ? labelFor(workChallenges, answers.challenge, t, empty) : empty;

  return (
    <div className="space-y-5">
      <div className="grid gap-5 lg:grid-cols-2">
        <Card aria-label={t("onboarding.steps.about")}>
          <CardHeader title={t("onboarding.steps.about")} />
          <dl>
            <ProfileDetailRow label={t("onboarding.roleLabel")} value={role?.title ?? empty} />
            <ProfileDetailRow label={t("onboarding.contextLabel")} value={careLabel} />
            <ProfileDetailRow label={t("onboarding.experienceLabel")} value={yearsLabel} />
          </dl>
        </Card>

        <Card aria-label={t("onboarding.steps.credentials")}>
          <CardHeader title={t("onboarding.steps.credentials")} />
          <dl>
            <ProfileDetailRow
              label={t("onboarding.credentials.certificates")}
              value={answers.certificateName ?? empty}
            />
            <ProfileDetailRow
              label={t("onboarding.credentials.diplomas")}
              value={answers.diplomaName ?? empty}
            />
          </dl>
        </Card>

        <Card aria-label={t("onboarding.steps.experience")}>
          <CardHeader title={t("onboarding.steps.experience")} />
          <dl>
            <ProfileDetailRow label={t("onboarding.domainsLabel")} value={domainsLabel} />
            <ProfileDetailRow
              label={t("onboarding.missingTopicLabel")}
              value={answers.missingTopic.trim() || empty}
            />
            <ProfileDetailRow label={t("onboarding.challengeLabel")} value={challengeLabel} />
          </dl>
        </Card>
      </div>

      <div>
        <Link
          className="inline-flex min-h-10 items-center rounded-xl border border-input bg-background px-4 text-sm font-medium shadow-xs hover:bg-muted focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-ring/25"
          data-control-id="profile.onboarding.open"
          to="/me/onboarding"
        >
          {t(persona.onboardingComplete ? "profile.reviewOnboarding" : "profile.openOnboarding")}
        </Link>
      </div>
    </div>
  );
}
